import { EntityKind, GroundState, SIXTEENTHS, type EntityState, type Item, type Rules } from "odyssey-shared";

/**
 * Finding ground items in the client's view (P2.8-T65, ADR-091's amendment for P2.8-T13, decision 3): the one E picks
 * up, the one under a click, what item one shows and which look it takes. The reach is the map server's own test for
 * a pick-up, so E sends only where the server would let it pass on reach.
 */

/**
 * How far a ground item may lie from the character's feet point, on each axis, for a pick-up to pass, in sixteenths:
 * one tile, the map server's reach for `pickUp`.
 */
export const GROUND_REACH = SIXTEENTHS;

/**
 * How a ground item is drawn: the page's own item while it is owned, another character's item while owned, or an
 * item anyone may pick up.
 */
export type GroundLook = "own" | "owned" | "public";

/**
 * How strong another character's owned item is drawn (the author's answer of 2026-10-10, "Dim less, add shadow"), and
 * its label with it (P2.8-T76).
 */
export const OWNED_ALPHA = 0.75;

/**
 * The strength a ground item and its label draw at for its look.
 *
 * @param look The look.
 * @returns `OWNED_ALPHA` for another character's owned item, 1 for the page's own and a public item.
 */
export function groundAlpha(look: GroundLook): number {
  return look === "owned" ? OWNED_ALPHA : 1;
}

/**
 * The ground item E picks up: the nearest in the client's view within `GROUND_REACH` of the point on both axes, by
 * straight distance, the first in the view's order on a tie.
 *
 * @param entities The client's view.
 * @param x The character's feet x, in sixteenths.
 * @param y The character's feet y, in sixteenths.
 * @returns The item's handle, or -1 for none in reach.
 * @example
 * nearestGround(view.entities, predicted.x, predicted.y);
 * // 41, a potion half a tile to the right
 */
export function nearestGround(entities: ReadonlyMap<number, EntityState>, x: number, y: number): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const state of entities.values()) {
    if (state.kind !== EntityKind.GroundItem) {
      continue;
    }
    const dx = state.x - x;
    const dy = state.y - y;
    if (Math.abs(dx) > GROUND_REACH || Math.abs(dy) > GROUND_REACH) {
      continue;
    }
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      best = state.handle;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The ground item under a point: of the items whose drawn square, `half` either side of the item's point, holds the
 * point, the one whose point is nearest, the first in the view's order on a tie.
 *
 * @param entities The client's view.
 * @param x The point's x, in sixteenths.
 * @param y The point's y, in sixteenths.
 * @param half Half the drawn square's side, in sixteenths.
 * @returns The item's handle, or -1 for none.
 */
export function groundUnder(entities: ReadonlyMap<number, EntityState>, x: number, y: number, half: number): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const state of entities.values()) {
    if (state.kind !== EntityKind.GroundItem) {
      continue;
    }
    const dx = state.x - x;
    const dy = state.y - y;
    if (Math.abs(dx) > half || Math.abs(dy) > half) {
      continue;
    }
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      best = state.handle;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The item a ground item's appearance names: its place in the rules' items counting from 1 (`groundAppearance`), read
 * by the rules the page shares with the server.
 *
 * @param rules The page's rules.
 * @param appearance The record's appearance.
 * @returns The item, or null for 0 or a place past the rules' items, which a page on other rules can meet.
 */
export function groundItemOf(rules: Rules, appearance: number): Item | null {
  return rules.tables.items[appearance - 1] ?? null;
}

/**
 * The look a ground item takes: public once its state says so; while owned, the page's own when it holds the mark or the record is in state 2.
 *
 * @param state The item's record.
 * @param own Whether the page holds its mark.
 * @returns The look.
 */
export function groundLook(state: Pick<EntityState, "anim">, own: boolean): GroundLook {
  if (state.anim === GroundState.Public) {
    return "public";
  }
  return own ? "own" : "owned";
}
